import { useMemo } from 'react'
import { P, match } from 'ts-pattern'
import type {
  FindingAggregateResponseStatistics,
  FixCounts,
  ScaCounts,
  ScanResponse,
  TriageCounts,
} from '../../utils/api-client/api-schemas'
import { isScaTool } from '../../utils/detector-utils'
import { getFixableFindingsCount } from '../../utils/finding-statistics'
import type { CompositionBarSegments } from '../tables/finding-quick-view-config'
import { type FindingQuickViewId, getFindingQuickViewConfig } from '../tables/finding-quick-view-config'
import { CompositionBarWithLabel } from './composition-bar-with-label'
import {
  type FixSegment,
  type ScaSegment,
  type TriageSegment,
  getQuickViewEmptyMessage,
  getQuickViewNonClickableSegments,
  getQuickViewTotalFindings,
} from './composition-utils'
import * as compositionStyles from './quick-view-composition-bars.css'

export type QuickViewCompositionBarsProps = {
  scan?: ScanResponse
  filteredStatistics?: FindingAggregateResponseStatistics
  activeFilterStatistics?: FindingAggregateResponseStatistics
  isFilteredDataTransitioning?: boolean
  quickViewId: FindingQuickViewId
  onTriageSegmentClick?: (label: TriageSegment) => unknown
  onFixSegmentClick?: (label: FixSegment) => unknown
  onScaSegmentClick?: (label: ScaSegment) => unknown
  triageSelectedSegments?: Iterable<TriageSegment>
  fixSelectedSegments?: Iterable<FixSegment>
  scaSelectedSegments?: Iterable<ScaSegment>
}

export const QuickViewCompositionBars: React.FC<QuickViewCompositionBarsProps> = ({
  scan,
  filteredStatistics,
  activeFilterStatistics,
  isFilteredDataTransitioning,
  quickViewId,
  onTriageSegmentClick,
  onFixSegmentClick,
  onScaSegmentClick,
  triageSelectedSegments = new Set<TriageSegment>(),
  fixSelectedSegments = new Set<FixSegment>(),
  scaSelectedSegments = new Set<ScaSegment>(),
}) => {
  const isSca = scan ? isScaTool(scan.detector) : false
  // Bar segment proportions always use base filtered statistics (default quick view)
  const totalFindings = useMemo(
    () => (filteredStatistics ? getQuickViewTotalFindings(filteredStatistics, { isSca, quickViewId }) : 0),
    [filteredStatistics, isSca, quickViewId]
  )
  // Label values use active filter stats when filters are applied, otherwise fall back to base
  const effectiveStats = activeFilterStatistics ?? filteredStatistics
  const effectiveTotalFindings = useMemo(
    () => (effectiveStats ? getQuickViewTotalFindings(effectiveStats, { isSca, quickViewId }) : 0),
    [effectiveStats, isSca, quickViewId]
  )
  const compositionBarSegments = useMemo(
    () => getFindingQuickViewConfig(quickViewId).compositionBarSegments,
    [quickViewId]
  )
  const nonClickable = useMemo(() => getQuickViewNonClickableSegments(quickViewId), [quickViewId])

  if (!scan || !filteredStatistics) return null

  if (totalFindings === 0) {
    return <CompositionBarWithLabel type="empty" label={getQuickViewEmptyMessage(quickViewId)} />
  }

  const noAnalyzedFindingsInView = !!activeFilterStatistics && effectiveTotalFindings === 0

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%' }}>
        {isSca ? (
          <ScaCompositionBars
            labelStatistics={effectiveStats!}
            totalFindings={totalFindings}
            labelTotalFindings={effectiveTotalFindings}
            scan={scan}
            isFilteredDataTransitioning={isFilteredDataTransitioning}
            visibleSegments={compositionBarSegments?.sca}
            nonClickableSegments={nonClickable.sca}
            quickViewId={quickViewId}
            scaStatistics={filteredStatistics.sca}
            onScaSegmentClick={onScaSegmentClick}
            scaSelectedSegments={scaSelectedSegments}
            noAnalyzedFindingsInView={noAnalyzedFindingsInView}
          />
        ) : (
          <SastCompositionBars
            labelStatistics={effectiveStats!}
            totalFindings={totalFindings}
            labelTotalFindings={effectiveTotalFindings}
            scan={scan}
            isFilteredDataTransitioning={isFilteredDataTransitioning}
            compositionBarSegments={compositionBarSegments}
            nonClickable={nonClickable}
            quickViewId={quickViewId}
            triageStatistics={filteredStatistics.triage}
            fixStatistics={filteredStatistics.fix}
            onTriageSegmentClick={onTriageSegmentClick}
            onFixSegmentClick={onFixSegmentClick}
            triageSelectedSegments={triageSelectedSegments}
            fixSelectedSegments={fixSelectedSegments}
            noAnalyzedFindingsInView={noAnalyzedFindingsInView}
            hasActiveFilters={!!activeFilterStatistics}
          />
        )}
      </div>
    </div>
  )
}

type SastCompositionBarsProps = {
  labelStatistics: FindingAggregateResponseStatistics
  totalFindings: number
  labelTotalFindings: number
  scan: ScanResponse
  isFilteredDataTransitioning?: boolean
  compositionBarSegments: CompositionBarSegments | undefined
  nonClickable: { triage: TriageSegment[]; fix: FixSegment[]; sca: ScaSegment[] }
  quickViewId: FindingQuickViewId
  triageStatistics: TriageCounts
  fixStatistics: FixCounts
  onTriageSegmentClick?: (label: TriageSegment) => unknown
  onFixSegmentClick?: (label: FixSegment) => unknown
  triageSelectedSegments: Iterable<TriageSegment>
  fixSelectedSegments: Iterable<FixSegment>
  noAnalyzedFindingsInView?: boolean
  hasActiveFilters?: boolean
}

const SastCompositionBars: React.FC<SastCompositionBarsProps> = ({
  labelStatistics,
  totalFindings,
  labelTotalFindings,
  scan,
  isFilteredDataTransitioning,
  compositionBarSegments,
  nonClickable,
  quickViewId,
  triageStatistics,
  fixStatistics,
  onTriageSegmentClick,
  onFixSegmentClick,
  triageSelectedSegments,
  fixSelectedSegments,
  noAnalyzedFindingsInView,
  hasActiveFilters,
}) => {
  const showTriage = !compositionBarSegments || compositionBarSegments.triage !== undefined
  const showFix = !compositionBarSegments || compositionBarSegments.fix !== undefined
  const isDismissable = quickViewId === 'dismissable'
  const isUnanalyzedView = quickViewId === 'unanalyzed'

  if (noAnalyzedFindingsInView) {
    return (
      <div className={compositionStyles.compositionRow}>
        <CompositionBarWithLabel type="empty" label="No findings match filters" />
      </div>
    )
  }

  return (
    <>
      {showTriage && (
        <div className={compositionStyles.compositionRow}>
          {match(isUnanalyzedView)
            .with(true, () => (
              <CompositionBarWithLabel type="queued" label="Analysis has not been completed on these findings" />
            ))
            .with(false, () => (
              <CompositionBarWithLabel
                type="triage"
                statistics={triageStatistics}
                total={totalFindings}
                tool={scan.detector}
                isTransitioning={isFilteredDataTransitioning}
                visibleSegments={compositionBarSegments?.triage}
                nonClickableSegments={nonClickable.triage}
                onSegmentClick={onTriageSegmentClick}
                selectedSegments={triageSelectedSegments}
                labelProps={{
                  value: `${labelStatistics.triage.status.completed}/${labelTotalFindings}`,
                  description: 'findings were triaged',
                }}
              />
            ))
            .exhaustive()}
        </div>
      )}
      {showFix && (
        <div className={compositionStyles.compositionRow}>
          {match({
            isDismissable,
            noFixableFindings: getFixableFindingsCount(fixStatistics) === 0,
            noActiveFixableFindings:
              getFixableFindingsCount(fixStatistics) > 0 &&
              !!hasActiveFilters &&
              (getFixableFindingsCount(labelStatistics.fix) === 0 || labelStatistics.fix.outcome.has_fix === 0),
          })
            .with(
              P.union({ isDismissable: true }, { noFixableFindings: true }, { noActiveFixableFindings: true }),
              ({ isDismissable, noActiveFixableFindings }) => (
                <CompositionBarWithLabel
                  type="empty"
                  label={
                    isDismissable
                      ? 'Fixes are irrelevant to dismissable findings'
                      : noActiveFixableFindings
                        ? 'Fixes do not apply to the selected findings'
                        : 'Fixes apply to none of the findings'
                  }
                />
              )
            )
            .otherwise(() => (
              <CompositionBarWithLabel
                type="fix"
                statistics={fixStatistics}
                tool={scan.detector}
                isTransitioning={isFilteredDataTransitioning}
                visibleSegments={compositionBarSegments?.fix}
                nonClickableSegments={nonClickable.fix}
                onSegmentClick={onFixSegmentClick}
                selectedSegments={fixSelectedSegments}
                labelProps={{
                  value: `${labelStatistics.fix.outcome.has_fix}/${getFixableFindingsCount(labelStatistics.fix)}`,
                  description: 'findings have an available fix',
                }}
              />
            ))}
        </div>
      )}
    </>
  )
}

type ScaCompositionBarsProps = {
  labelStatistics: FindingAggregateResponseStatistics
  totalFindings: number
  labelTotalFindings: number
  scan: ScanResponse
  isFilteredDataTransitioning?: boolean
  visibleSegments?: ScaSegment[]
  nonClickableSegments: ScaSegment[]
  quickViewId: FindingQuickViewId
  scaStatistics: ScaCounts
  onScaSegmentClick?: (label: ScaSegment) => unknown
  scaSelectedSegments: Iterable<ScaSegment>
  noAnalyzedFindingsInView?: boolean
}

const ScaCompositionBars: React.FC<ScaCompositionBarsProps> = ({
  labelStatistics,
  totalFindings,
  labelTotalFindings,
  scan,
  isFilteredDataTransitioning,
  visibleSegments,
  nonClickableSegments,
  quickViewId,
  scaStatistics,
  onScaSegmentClick,
  scaSelectedSegments,
  noAnalyzedFindingsInView,
}) => {
  if (noAnalyzedFindingsInView) {
    return (
      <div className={compositionStyles.compositionRow}>
        <CompositionBarWithLabel type="empty" label="No findings match filters" />
      </div>
    )
  }

  return (
    <div className={compositionStyles.compositionRow}>
      {match(quickViewId)
        .with('sca-unanalyzed', () => (
          <CompositionBarWithLabel type="queued" label="Analysis has not been completed on these findings" />
        ))
        .otherwise(() => (
          <CompositionBarWithLabel
            type="sca"
            statistics={scaStatistics}
            total={totalFindings}
            tool={scan.detector}
            isTransitioning={isFilteredDataTransitioning}
            visibleSegments={visibleSegments}
            nonClickableSegments={nonClickableSegments}
            onSegmentClick={onScaSegmentClick}
            selectedSegments={scaSelectedSegments}
            labelProps={{
              value: `${labelStatistics.sca.status.completed}/${labelTotalFindings}`,
              description: 'findings were analyzed',
            }}
          />
        ))}
    </div>
  )
}
